import {ButtonGroup, closeCurrentDialog, DataStatus} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {useCreateAgreementOnFutureContractMutation} from '@dms/api/sales';
import {FileResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import {useDocumentTemplates} from '../../../hooks/useDocumentTemplates';
import {handleApiError} from '../../../utils/handleApiError';
import {
  DocumentTemplateBox,
  TemplateBoxTemplate,
} from '../../DocumentTemplateBox/DocumentTemplateBox';

interface FutureAgreementDialogProps {
  businessCaseId: string;
  customerId?: string | Nullish;
  onDocumentCreation: (document: FileResponseBody) => void;
}

export function FutureAgreementDialog(props: FutureAgreementDialogProps) {
  const [isDocumentSelected, setIsDocumentSelected] = useState(true);
  const [documentNote, setDocumentNote] = useState<string | null>();

  const [pickedDocumentTemplate, setPickedDocumentTemplate] = useState<TemplateBoxTemplate | null>(
    null
  );

  const [createFutureAgreement, {isLoading: isCreateFutureAgreementLoading}] =
    useCreateAgreementOnFutureContractMutation();

  const [getTemplatesByCode, {isLoading, isError}] = useDocumentTemplates();
  const {
    defaultTemplates,
    selectedTemplates,
    selectedAuthProfileLabel,
    primaryTemplate,
    definition,
  } = getTemplatesByCode('agreement-on-future');

  const handleSubmit = () => {
    if (!isDocumentSelected && isNotNil(primaryTemplate)) {
      return;
    }

    return createFutureAgreement({
      agreementOnFutureRequestBody: {
        templateId: pickedDocumentTemplate?.id ?? primaryTemplate!.id,
        businessCaseId: props.businessCaseId,
        note: documentNote ?? null,
      },
    })
      .unwrap()
      .then(props.onDocumentCreation)
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {i18n.t('entity.vehicle.labels.futureAgreementText')}
        </Text>
        <DocumentTemplateBox
          type={definition?.documentCode}
          title={i18n.t(`entity.document.labels.agreementOnFuture`)}
          templates={defaultTemplates}
          authorizationTemplates={selectedTemplates}
          authorizationProfileLabel={selectedAuthProfileLabel}
          selectedTemplate={pickedDocumentTemplate ?? primaryTemplate}
          isSelected={isDocumentSelected || isNotNil(primaryTemplate)}
          onSelect={setIsDocumentSelected}
          onTemplateChange={setPickedDocumentTemplate}
          onNoteChange={setDocumentNote}
          isWithNote
        />
        <ButtonGroup
          align="right"
          buttons={[
            {
              title: i18n.t('general.actions.cancel'),
              onClick: closeCurrentDialog,
              type: 'button',
              variant: 'secondary',
            },
            {
              title: i18n.t('general.actions.confirm'),
              onClick: handleSubmit,
              type: 'button',
              isLoading: isCreateFutureAgreementLoading,
            },
          ]}
        />
      </VStack>
    </DataStatus>
  );
}
